import { createFileRoute, redirect } from "@tanstack/react-router";
import BlogPost from "@/pages/BlogPost";
import { routeHead } from "@/lib/seo/route-head";
import { segmentByLegacySlug } from "@/lib/segmentStats";
import { isReviewedBlogSlug, reviewedBlogHead } from "@/lib/blogEditorial";

export const Route = createFileRoute("/blog/$slug/")({
  // The blog corpus (~190KB of source) is loaded on demand here instead of being
  // imported at module scope, which would pull it into the entry bundle.
  loader: async ({ params, context }) => {
    const seg = segmentByLegacySlug(params.slug);
    if (seg) throw redirect({ href: `/statistici/${seg.slug}`, statusCode: 301 });
    const { getBlogPostBySlug } = await import("@/data/blogPosts");
    const post = getBlogPostBySlug(params.slug);
    if (!post) return { post: null };
    if (isReviewedBlogSlug(post.slug)) {
      const { blogStockQueryOptions } = await import('@/lib/blogStockQuery');
      // Stock failure never prevents the editorial page from rendering.
      await context.queryClient.ensureQueryData(blogStockQueryOptions(post.slug)).catch(() => undefined);
    }
    return {
      post: {
        slug: post.slug,
        title: post.title,
        seoTitle: post.seoTitle ?? null,
        excerpt: post.excerpt,
        date: post.date,
        dateModified: post.dateModified ?? null,
        author: post.author,
        category: post.category,
        image: post.image ?? null,
        ogImage: post.ogImage ?? null,
      },
    };
  },
  head: ({ params, loaderData }) => {
    const base = routeHead("/blog/$slug/", params as Record<string, string>);
    const post = loaderData?.post;
    if (!post) return base;
    if (isReviewedBlogSlug(post.slug)) return reviewedBlogHead(post);

    const url = `https://roautomarket.ro/blog/${post.slug}`;
    // Use the article's own title and excerpt instead of the slug-derived fallback
    // (which produced titles like "Blog Bmw Diesel în România").
    const fullTitle = post.seoTitle || `${post.title} | RO Auto Market`;
    const replaced = new Set(["title", "description", "og:title", "og:description", "twitter:title", "twitter:description", "og:type"]);
    if (post.ogImage) { replaced.add("og:image"); replaced.add("twitter:image"); }
    const keep = (base.meta ?? []).filter(
      (m: Record<string, string>) => m.title === undefined && !replaced.has(m.name) && !replaced.has(m.property),
    );
    return {
      ...base,
      meta: [
        { title: fullTitle },
        { name: "title", content: fullTitle },
        { name: "description", content: post.excerpt },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.excerpt },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.excerpt },
        ...keep,
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: post.date },
        { property: "article:author", content: post.author },
        ...(post.ogImage
          ? [
              { property: "og:image", content: post.ogImage },
              { name: "twitter:image", content: post.ogImage },
            ]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.image
              ? `https://roautomarket.ro${post.image}`
              : "https://roautomarket.ro/hero-roautomarket.jpg",
            datePublished: post.date,
            dateModified: post.dateModified || post.date,
            articleSection: post.category,
            author: {
              "@type": "Person",
              name: post.author,
              url: "https://roautomarket.ro/despre-noi",
            },
            publisher: {
              "@type": "Organization",
              name: "RO Auto Market",
              logo: {
                "@type": "ImageObject",
                url: "https://roautomarket.ro/og-roautomarket.png",
              },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
      ],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <BlogPost />
  );
}
